"use server";

import { headers } from "next/headers";
import { z } from "zod";
import { AppointmentStatus, InquiryForm, InquiryReason, PreferredContact, ServiceInterest } from "@prisma/client";
import { Prisma } from "@prisma/client";
import { db } from "@/lib/db";
import { getTenant } from "@/lib/tenant";
import { rateLimit, clientIp } from "@/lib/rate-limit";
import { hashIp } from "@/lib/hash";
import { requiredEnv } from "@/lib/env";
import { honeypotFilled, verifyCaptcha } from "@/lib/captcha";
import { appointmentSource, isMetricName } from "@/lib/source";
import { sendTeaser } from "@/lib/ses";
import { SMS_CONSENT_VERSION } from "@/lib/home-form";
import { openSlots } from "@/lib/slots";
import { addMinutes } from "date-fns";

const inquirySchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(7).max(40),
  preferredContact: z.nativeEnum(PreferredContact),
  reason: z.nativeEnum(InquiryReason),
  message: z.string().trim().min(1).max(4000),
  source: z.string().trim().max(40).optional(),
  utmSource: z.string().trim().max(80).optional(),
  utmMedium: z.string().trim().max(80).optional(),
  utmCampaign: z.string().trim().max(80).optional(),
  captchaToken: z.string().optional(),
});

class SlotTakenError extends Error {}

const homeSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(7).max(40),
  serviceInterest: z.nativeEnum(ServiceInterest),
  message: z.string().trim().min(1).max(4000),
  smsTransactional: z.boolean(),
  smsMarketing: z.boolean(),
  source: z.string().trim().max(40).optional(),
  utmSource: z.string().trim().max(80).optional(),
  utmMedium: z.string().trim().max(80).optional(),
  utmCampaign: z.string().trim().max(80).optional(),
  captchaToken: z.string().optional(),
});

const bookSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(7).max(40),
  serviceId: z.string().min(1),
  providerId: z.string().min(1),
  start: z.string().min(1),
  notes: z.string().trim().max(2000).optional(),
  source: z.string().trim().max(40).optional(),
  utmSource: z.string().trim().max(80).optional(),
  utmMedium: z.string().trim().max(80).optional(),
  utmCampaign: z.string().trim().max(80).optional(),
  captchaToken: z.string().optional(),
});

async function guardPublic(formData: FormData, kind: string) {
  requiredEnv("DATABASE_URL");
  requiredEnv("AUTH_SECRET");
  const hdrs = await headers();
  const ip = clientIp(hdrs);
  if (!rateLimit(`${kind}:${ip}`)) {
    return { ok: false as const, error: "Too many attempts. Please wait and try again." };
  }
  if (honeypotFilled(formData.get("website"))) {
    return { ok: false as const, error: "Unable to submit right now." };
  }
  const captchaOk = await verifyCaptcha(String(formData.get("captchaToken") || ""));
  if (!captchaOk) {
    return { ok: false as const, error: "Verification failed. Please refresh and try again." };
  }
  return { ok: true as const, ip, userAgent: hdrs.get("user-agent") ?? "" };
}

export async function submitInquiry(formData: FormData) {
  const gate = await guardPublic(formData, "inquiry");
  if (!gate.ok) return { ok: false, error: gate.error };

  const parsed = inquirySchema.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    phone: formData.get("phone"),
    preferredContact: formData.get("preferredContact") || "call",
    reason: formData.get("reason"),
    message: formData.get("message"),
    source: formData.get("source") || "website",
    utmSource: formData.get("utmSource") || undefined,
    utmMedium: formData.get("utmMedium") || undefined,
    utmCampaign: formData.get("utmCampaign") || undefined,
    captchaToken: formData.get("captchaToken") || undefined,
  });
  if (!parsed.success) {
    return { ok: false, error: "Please check your details and try again." };
  }

  const tenant = await getTenant();
  const inquiry = await db.inquiry.create({
    data: {
      tenantId: tenant.id,
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      preferredContact: parsed.data.preferredContact,
      reason: parsed.data.reason,
      message: parsed.data.message,
      source: parsed.data.source || "website",
      utmSource: parsed.data.utmSource,
      utmMedium: parsed.data.utmMedium,
      utmCampaign: parsed.data.utmCampaign,
      ipHash: hashIp(gate.ip, requiredEnv("AUTH_SECRET")),
      userAgent: gate.userAgent.slice(0, 300),
    },
    select: { id: true },
  });

  await db.metricEvent.create({
    data: { tenantId: tenant.id, name: "form_submit", source: parsed.data.source || "website", path: "/contact" },
  });

  await sendTeaser({
    to: tenant.notifyEmail,
    from: tenant.sesFromAddress,
    configurationSet: tenant.sesConfigurationSet,
    subject: "New inquiry received",
    adminPath: `/admin/inquiries/${inquiry.id}`,
  });

  return { ok: true };
}

function boxChecked(value: FormDataEntryValue | null) {
  return value === "on" || value === "true" || value === "1";
}

export async function submitHomeInquiry(formData: FormData) {
  const gate = await guardPublic(formData, "home-inquiry");
  if (!gate.ok) return { ok: false, error: gate.error };

  const parsed = homeSchema.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    phone: formData.get("phone"),
    serviceInterest: formData.get("serviceInterest"),
    message: formData.get("message"),
    smsTransactional: boxChecked(formData.get("smsTransactional")),
    smsMarketing: boxChecked(formData.get("smsMarketing")),
    source: formData.get("source") || "website",
    utmSource: formData.get("utmSource") || undefined,
    utmMedium: formData.get("utmMedium") || undefined,
    utmCampaign: formData.get("utmCampaign") || undefined,
    captchaToken: formData.get("captchaToken") || undefined,
  });
  if (!parsed.success) {
    return { ok: false, error: "Please check your details and try again." };
  }

  const tenant = await getTenant();
  const consented = parsed.data.smsTransactional || parsed.data.smsMarketing;
  const inquiry = await db.inquiry.create({
    data: {
      tenantId: tenant.id,
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      reason: InquiryReason.new_patient,
      form: InquiryForm.home,
      serviceInterest: parsed.data.serviceInterest,
      message: parsed.data.message,
      source: parsed.data.source || "website",
      utmSource: parsed.data.utmSource,
      utmMedium: parsed.data.utmMedium,
      utmCampaign: parsed.data.utmCampaign,
      smsTransactionalConsent: parsed.data.smsTransactional,
      smsMarketingConsent: parsed.data.smsMarketing,
      smsConsentAt: consented ? new Date() : null,
      smsConsentVersion: consented ? SMS_CONSENT_VERSION : null,
      ipHash: hashIp(gate.ip, requiredEnv("AUTH_SECRET")),
      userAgent: gate.userAgent.slice(0, 300),
    },
    select: { id: true },
  });

  await db.metricEvent.create({
    data: {
      tenantId: tenant.id,
      name: "home_form_submit",
      source: parsed.data.source || "website",
      path: "/inquire",
    },
  });

  await sendTeaser({
    to: tenant.notifyEmail,
    from: tenant.sesFromAddress,
    configurationSet: tenant.sesConfigurationSet,
    subject: "New home page inquiry",
    adminPath: `/admin/inquiries/${inquiry.id}`,
  });

  return { ok: true };
}

export async function submitBooking(formData: FormData) {
  const gate = await guardPublic(formData, "book");
  if (!gate.ok) return { ok: false, error: gate.error };

  const parsed = bookSchema.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    phone: formData.get("phone"),
    serviceId: formData.get("serviceId"),
    providerId: formData.get("providerId"),
    start: formData.get("start"),
    notes: formData.get("notes") || "",
    source: formData.get("source") || "website",
    utmSource: formData.get("utmSource") || undefined,
    utmMedium: formData.get("utmMedium") || undefined,
    utmCampaign: formData.get("utmCampaign") || undefined,
  });
  if (!parsed.success) {
    return { ok: false, error: "Please check your booking details and try again." };
  }

  const tenant = await getTenant();
  const [service, provider] = await Promise.all([
    db.service.findFirst({ where: { id: parsed.data.serviceId, tenantId: tenant.id, active: true } }),
    db.provider.findFirst({ where: { id: parsed.data.providerId, tenantId: tenant.id, active: true } }),
  ]);
  if (!service || !provider) {
    return { ok: false, error: "That service or provider is not available." };
  }
  if (!service.roles.includes(provider.role)) {
    return { ok: false, error: "That provider does not offer this service." };
  }

  const start = new Date(parsed.data.start);
  if (Number.isNaN(start.getTime()) || start <= new Date()) {
    return { ok: false, error: "Please choose a future time." };
  }

  const dateStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: tenant.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(start);
  const { slots } = await openSlots({
    tenantId: tenant.id,
    providerId: provider.id,
    durationMin: service.durationMin,
    dateStr,
    timezone: tenant.timezone,
    horizonDays: tenant.bookingHorizonDays,
  });
  if (!slots.some((slot) => slot.start === start.toISOString())) {
    return { ok: false, error: "That time is no longer open. Please pick another slot." };
  }

  const end = addMinutes(start, service.durationMin);
  const source = parsed.data.source || "website";

  try {
    const created = await db.$transaction(async (tx) => {
      await tx.$executeRaw`SELECT pg_advisory_xact_lock(hashtext(${provider.id}))`;
      const clash = await tx.appointment.findFirst({
        where: {
          tenantId: tenant.id,
          providerId: provider.id,
          status: AppointmentStatus.booked,
          start: { lt: end },
          end: { gt: start },
        },
        select: { id: true },
      });
      if (clash) throw new SlotTakenError();
      const inquiry = await tx.inquiry.create({
        data: {
          tenantId: tenant.id,
          name: parsed.data.name,
          email: parsed.data.email,
          phone: parsed.data.phone,
          reason: InquiryReason.new_patient,
          form: InquiryForm.booking,
          message: parsed.data.notes || "Book Now request",
          source,
          utmSource: parsed.data.utmSource,
          utmMedium: parsed.data.utmMedium,
          utmCampaign: parsed.data.utmCampaign,
          status: "scheduled",
          ipHash: hashIp(gate.ip, requiredEnv("AUTH_SECRET")),
          userAgent: gate.userAgent.slice(0, 300),
        },
      });
      const appointment = await tx.appointment.create({
        data: {
          tenantId: tenant.id,
          providerId: provider.id,
          serviceId: service.id,
          inquiryId: inquiry.id,
          start,
          end,
          status: AppointmentStatus.booked,
          source: appointmentSource(source),
          name: parsed.data.name,
          email: parsed.data.email,
          phone: parsed.data.phone,
          notes: parsed.data.notes || "",
          utmSource: parsed.data.utmSource,
          utmMedium: parsed.data.utmMedium,
          utmCampaign: parsed.data.utmCampaign,
        },
        select: { id: true },
      });
      await tx.metricEvent.create({
        data: { tenantId: tenant.id, name: "book_completed", source, path: "/book" },
      });
      return appointment;
    });

    await sendTeaser({
      to: tenant.notifyEmail,
      from: tenant.sesFromAddress,
      configurationSet: tenant.sesConfigurationSet,
      subject: "New booking received",
      adminPath: `/admin/appointments?open=${created.id}`,
    });
    return { ok: true };
  } catch (error) {
    if (
      error instanceof SlotTakenError ||
      (error instanceof Prisma.PrismaClientKnownRequestError && error.code === "P2002")
    ) {
      return { ok: false, error: "That time was just taken. Please pick another slot." };
    }
    console.error("[book] persist failed");
    return { ok: false, error: "Unable to complete that booking. Please try again." };
  }
}

export async function trackMetric(name: string, source: string, path?: string) {
  if (!isMetricName(name)) return;
  const hdrs = await headers();
  const ip = clientIp(hdrs);
  if (!rateLimit(`metric:${ip}`, 40, 10 * 60 * 1000)) return;
  const tenant = await getTenant();
  await db.metricEvent.create({
    data: {
      tenantId: tenant.id,
      name,
      source: source.slice(0, 40) || "website",
      path: path?.slice(0, 120),
    },
  });
}
